'use client'

import { useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { ArrowUpRightSVG } from '@/src/ui/AllSVG'
import ImageComponent from '../../common/ImageComponent'
import { Button } from '@heroui/react'
import { formatAmount } from '@/src/utils/formatters'
import { getMembershipPath } from '@/src/routes/routes'
import { useRouter } from 'nextjs-toploader/app'

gsap.registerPlugin(ScrollTrigger)

const FREE_SHIPPING_PERK = 'Free shipping'
const FREE_SHIPPING_BENEFIT = 'Free express shipping on all orders, no minimum spend'

interface MembershipSectionProps {
  membershipRate?: number;
  membershipPrice?: number;
  currency?: string;
}

export default function MembershipSection({ membershipRate, membershipPrice, currency }: MembershipSectionProps) {
  const sectionRef = useRef<HTMLDivElement>(null)
  const router = useRouter()
  const showFreeShipping = (membershipRate ?? 1) <= 0

  const perks = [
    'Members-only pricing',
    ...(showFreeShipping ? [FREE_SHIPPING_PERK] : []),
    'Early access',
    'Up to 70% off',
  ]

  const benefits = [
    'Exclusive member pricing on every item in the collection',
    ...(showFreeShipping ? [FREE_SHIPPING_BENEFIT] : []),
    'Priority early access to new arrivals and seasonal drops',
  ]

  useGSAP(() => {
    // Left card — fade + rise on enter
    gsap.from('.ms-card', {
      opacity: 0,
      y: 60,
      duration: 0.9,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: '.ms-card',
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    })

    // Perks tags — staggered pop in
    gsap.from('.ms-perk', {
      opacity: 0,
      scale: 0.8,
      duration: 0.35,
      stagger: 0.07,
      ease: 'back.out(1.7)',
      scrollTrigger: {
        trigger: '.ms-perk',
        start: 'top 90%',
        toggleActions: 'play none none reverse',
      },
    })

    // Benefits — staggered slide from left
    gsap.from('.ms-benefit', {
      opacity: 0,
      x: -24,
      duration: 0.5,
      stagger: 0.1,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: '.ms-benefit',
        start: 'top 90%',
        toggleActions: 'play none none reverse',
      },
    })

    // Right headings — text reveal (clip from bottom)
    gsap.from('.ms-reveal', {
      yPercent: 100,
      duration: 0.8,
      stagger: 0.15,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: '.ms-right',
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    })

    // Right label + description — fade up
    gsap.from('.ms-fade', {
      opacity: 0,
      y: 20,
      duration: 0.7,
      stagger: 0.12,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: '.ms-right',
        start: 'top 85%',
        toggleActions: 'play none none reverse',
      },
    })

    // Illustration — fade + subtle scale up
    gsap.from('.ms-illustration', {
      opacity: 0,
      scale: 0.92,
      duration: 1,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: '.ms-illustration',
        start: 'top 90%',
        toggleActions: 'play none none reverse',
      },
    })
  }, { scope: sectionRef })

  return (
    <>
      <div ref={sectionRef} className="custom_container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-y-6 gap-x-20 items-center sm:w-[90%] mx-auto">
          {/* Left — dark membership card */}
          <div className="ms-card bg-body-color rounded-2xl p-8 md:p-10 text-white space-y-6">
            {/* Badge */}
            <p className="bg_frosted_tag w-fit">VEM Membership</p>

            {/* Title + Price */}
            <div className="flex items-start justify-between gap-4">
              <h2 className="tertiary_heading">
                Vallote Annual{' '}
                <span className="font-secondary italic font-bold">Membership</span>
              </h2>
              <div className="text-right shrink-0">
                <p className="tertiary_heading">{formatAmount(membershipPrice ?? 0, currency ?? '')}</p>
                <p className="text-white/50 text-xs mt-1">/ year</p>
              </div>
            </div>

            {/* Perks tags */}
            <div className="flex flex-wrap gap-2 border-b border-white/12 pb-6">
              {perks.map((perk) => (
                <span key={perk} className="ms-perk bg_frosted_tag">
                  • {perk}
                </span>
              ))}
            </div>

            {/* Benefits list */}
            <ul className="space-y-2">
              {benefits.map((benefit) => (
                <li key={benefit} className="ms-benefit flex items-start gap-2 text-sm text-white/80">
                  <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-white/50 shrink-0" />
                  {benefit}
                </li>
              ))}
            </ul>

            {/* CTA */}
            <Button onClick={() => router.push(getMembershipPath())} className="global_btn bg_frosted rounded_full">
              Become a Member <ArrowUpRightSVG />
            </Button>
          </div>

          {/* Right — light info panel */}
          <div className="ms-right flex flex-col items-center lg:items-start justify-center gap-2 px-2">
            {/* Label */}
            <p className="ms-fade text-xs rounded-full bg-body-color/7 text-body-color/60 border border-body-color/12 w-fit py-1.5 px-2.5">
              Membership
            </p>

            {/* Heading — text reveal */}
            <div>
              <h2 className="secondary_heading font-medium! overflow-hidden">
                <span className="ms-reveal inline-block">One membership.</span>
              </h2>
              <h2 className="secondary_heading font-secondary italic font-medium! overflow-hidden">
                <span className="ms-reveal inline-block">Unlimited access.</span>
              </h2>
            </div>

            {/* Description */}
            <p className="ms-fade text-body-color/45 max-w-sm">
              Exclusive pricing on every piece in our collection.
              Personalised benefits. One simple annual fee.
            </p>

            {/* Illustration */}
            <div className="ms-illustration">
              <ImageComponent
                src="/images/common/clothes-illustration.svg"
                alt="Membership"
                object_cover={false}
                priority={true}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
